import React from "react";

import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import {
  DependencyItem,
  DependencyList,
} from "src/base-components/DependencyList";
import { FlowWithMatchingVersions } from "src/clients/flow-api";
import { Modal } from "src/design-system/Modal";
import { DraftPill, PublishedPill } from "src/flow/Pills";
import { getBaseUrl, getUrlToAuthoringPage } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type DeleteWithDependenciesModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  publishedFlows: FlowWithMatchingVersions[] | undefined;
  draftFlows: FlowWithMatchingVersions[] | undefined;
  blockingTitle: string;
  blockingDescription: string;
  blockingMessage: string;
  draftWarningMessage: string;
  confirmationTitle: string;
  confirmationDescription: string;
  confirmationDataLoc: string;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
};

export const DeleteWithDependenciesModal: React.FC<
  DeleteWithDependenciesModalProps
> = ({
  isOpen,
  isLoading,
  publishedFlows,
  draftFlows,
  blockingTitle,
  blockingDescription,
  blockingMessage,
  draftWarningMessage,
  confirmationTitle,
  confirmationDescription,
  confirmationDataLoc,
  onClose,
  onConfirm,
}) => {
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId: string }>();

  const hasPublishedFlows =
    !isLoading && publishedFlows && publishedFlows.length > 0;
  const hasDraftFlows = !isLoading && draftFlows && draftFlows.length > 0;

  const toDependencyItems = (
    flows: FlowWithMatchingVersions[] | undefined,
    pill: React.ReactNode,
  ): DependencyItem[] =>
    (flows ?? []).flatMap((flow) =>
      flow.versions.map((version) => ({
        id: version.id,
        name: `${flow.name} - ${version.name}`,
        group: "Flows",
        href:
          getBaseUrl() +
          getUrlToAuthoringPage(orgId, wsId, flow.id, version.id),
        statusPill: pill,
      })),
    );

  return (
    <>
      <Modal open={isOpen && !!hasPublishedFlows} onClose={onClose}>
        <Modal.Header description={blockingDescription}>
          {blockingTitle}
        </Modal.Header>
        <Modal.Content>
          <DependencyList
            dataLoc="referencing-flows"
            items={toDependencyItems(
              publishedFlows,
              <PublishedPill withIcon />,
            )}
          />
          <p className="mt-2.5 font-inter-normal-13px text-gray-500">
            {blockingMessage}
          </p>
        </Modal.Content>
        <Modal.Footer primaryButton={false} />
      </Modal>

      <ConfirmationModal
        confirmationButtonText="Delete"
        dataLoc={confirmationDataLoc}
        description={
          hasDraftFlows
            ? `${confirmationDescription} ${draftWarningMessage}`
            : confirmationDescription
        }
        open={isOpen && !hasPublishedFlows && !isLoading}
        title={confirmationTitle}
        variant="danger"
        onClose={onClose}
        onConfirm={onConfirm}
      >
        {hasDraftFlows && (
          <div className="mb-4">
            <DependencyList
              dataLoc="draft-referencing-flows"
              items={toDependencyItems(draftFlows, <DraftPill withIcon />)}
            />
          </div>
        )}
      </ConfirmationModal>
    </>
  );
};
